/**
 * entrega-estimada.js
 *
 * Añadir en theme.liquid con:
 *   <script src="{{ 'entrega-estimada.js' | asset_url }}" defer></script>
 *
 * Requiere que el snippet entrega-estimada.liquid esté presente en el DOM
 * (carrito o cart drawer). Si el carrito se renderiza vía Section Rendering
 * API tras cada cambio de cantidad/añadir producto, basta con llamar a
 * window.EntregaEstimada.refresh() después de inyectar el HTML nuevo.
 * Si no se llama manualmente, un MutationObserver detecta el cambio igualmente.
 */
(function () {
  const NOMBRES_ATRIBUTO = {
    fecha: "fecha_estimada_entrega", // ISO yyyy-mm-dd, para Flow / metafield
    texto: "fecha_estimada_entrega_texto", // texto ya formateado, para el email
    locale: "fecha_estimada_entrega_locale", // ej. "es" o "en-GB", para que el email sepa el idioma
  };

  let festivosCache = null;
  let festivosPromesaEnCurso = null;
  let ultimaFechaGuardada = null;

  // La hora de corte es la hora del almacén (Madrid), no la del navegador
  // del cliente — si alguien compra desde otro huso horario, comparar
  // contra su hora local daría un resultado distinto al real. Mismo
  // mecanismo que en entrega-estimada-producto.js.
  function ahoraEnMadrid() {
    const partes = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Madrid",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const obj = {};
    partes.forEach((p) => (obj[p.type] = p.value));
    const hora = obj.hour === "24" ? "00" : obj.hour;
    return new Date(`${obj.year}-${obj.month}-${obj.day}T${hora}:${obj.minute}:${obj.second}`);
  }

  function getContenedores() {
    return document.querySelectorAll("[data-entrega-estimada]");
  }

  async function cargarFestivos(url) {
    if (festivosCache) return festivosCache;
    // Si ya hay una carga en curso (p. ej. DOMContentLoaded y pageshow disparando
    // refresh() casi a la vez en la carga inicial de la página), se reutiliza la
    // misma promesa en vez de lanzar una segunda petición idéntica.
    if (festivosPromesaEnCurso) return festivosPromesaEnCurso;

    festivosPromesaEnCurso = (async () => {
      try {
        const res = await fetch(url);
        const data = await res.json();
        const todos = [
          ...(data.nacionales || []),
          ...(data.regionales_andalucia || []),
          ...(data.locales_chiclana_de_la_frontera || []),
        ];
        festivosCache = new Set(todos);
      } catch (e) {
        console.error("entrega-estimada: no se pudieron cargar los festivos", e);
        festivosCache = new Set();
      }
      return festivosCache;
    })();

    return festivosPromesaEnCurso;
  }

  function esFinDeSemana(date) {
    const d = date.getDay();
    return d === 0 || d === 6;
  }

  function toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function sumarDiasLaborables(fechaInicio, diasLaborables, festivos) {
    const fecha = new Date(fechaInicio);
    let restantes = diasLaborables;
    while (restantes > 0) {
      fecha.setDate(fecha.getDate() + 1);
      if (esFinDeSemana(fecha)) continue;
      if (festivos.has(toISO(fecha))) continue;
      restantes -= 1;
    }
    return fecha;
  }

  // Misma lógica de hora de corte que la página de producto (ver
  // entrega-estimada-producto.js): antes de la hora de corte, se cuenta
  // desde hoy; después, desde el siguiente día laborable. Se añadió aquí
  // para que el carrito y la página de producto den siempre la misma
  // fecha para el mismo plazo — antes el carrito no tenía esta lógica y
  // podía mostrar una fecha distinta a la de producto pasado el mediodía.
  function calcularBase(hoy, horaCorte, festivos) {
    const corteHoy = new Date(hoy);
    corteHoy.setHours(horaCorte, 0, 0, 0);

    if (hoy < corteHoy) {
      return new Date(hoy);
    }
    return sumarDiasLaborables(hoy, 1, festivos);
  }

  function formatearFecha(date, locale) {
    return new Intl.DateTimeFormat(locale || "es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  function obtenerMaxDias(contenedor) {
    const lis = contenedor.querySelectorAll("[data-entrega-estimada-dias] li");
    let max = 0;
    lis.forEach((li) => {
      const dias = parseInt(li.getAttribute("data-dias"), 10);
      if (!isNaN(dias) && dias > max) max = dias;
    });
    return max;
  }

  async function guardarAtributoCarrito(isoFecha, textoFecha, locale) {
    const claveDedupe = isoFecha + "|" + locale;
    if (ultimaFechaGuardada === claveDedupe) return; // evita llamadas repetidas
    ultimaFechaGuardada = claveDedupe;
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            [NOMBRES_ATRIBUTO.fecha]: isoFecha,
            [NOMBRES_ATRIBUTO.texto]: textoFecha,
            [NOMBRES_ATRIBUTO.locale]: locale || "es",
          },
        }),
      });
    } catch (e) {
      console.error("entrega-estimada: no se pudo guardar el atributo del carrito", e);
    }
  }

  // Registro global compartido con entrega-estimada-producto.js: si ambos
  // scripts coexisten en la misma página (ej. carrito + drawer visible en
  // una página de producto), cada uno debe poder desconectar TAMBIÉN el
  // observer del otro antes de escribir, no solo el suyo propio.
  window.__entregaEstimadaObservers = window.__entregaEstimadaObservers || [];

  function desconectarObserversCompartidos() {
    window.__entregaEstimadaObservers.forEach((o) => o.disconnect());
  }

  function reconectarObserversCompartidos() {
    window.__entregaEstimadaObservers.forEach((o) =>
      o.observe(document.body, { childList: true, subtree: true })
    );
  }

  let observer = null;

  async function refresh() {
    // data-entrega-estimada puede existir varias veces a la vez (carrito de
    // página + drawer del header). Se actualizan todos, pero el atributo de
    // carrito solo se guarda una vez gracias al dedupe en guardarAtributoCarrito.
    const contenedores = getContenedores();
    if (contenedores.length === 0) return;

    let festivosUrlUsada = null;
    let localeUsado = null;
    let horaCorte = 12;
    let maxDiasGlobal = 0;

    contenedores.forEach((contenedor) => {
      const maxDias = obtenerMaxDias(contenedor);
      if (maxDias > maxDiasGlobal) maxDiasGlobal = maxDias;
      if (!festivosUrlUsada) {
        festivosUrlUsada = contenedor.getAttribute("data-festivos-url");
      }
      if (!localeUsado) {
        localeUsado = contenedor.getAttribute("data-locale");
      }
      const horaCorteAttr = contenedor.getAttribute("data-hora-corte");
      if (horaCorteAttr) horaCorte = parseInt(horaCorteAttr, 10) || 12;
    });

    let textoFecha = null;
    let isoFecha = null;

    if (maxDiasGlobal > 0) {
      const festivos = await cargarFestivos(festivosUrlUsada);
      const hoy = ahoraEnMadrid();
      const base = calcularBase(hoy, horaCorte, festivos);
      const fechaEntrega = sumarDiasLaborables(base, maxDiasGlobal, festivos);
      textoFecha = formatearFecha(fechaEntrega, localeUsado);
      isoFecha = toISO(fechaEntrega);
    }

    // Desconectamos TODOS los observers registrados (el propio y, si
    // coexiste en la misma página, el de entrega-estimada-producto.js)
    // antes de tocar el DOM. Si solo desconectáramos el propio, el otro
    // script detectaría esta escritura como una mutación externa, volvería
    // a ejecutarse, y al escribir él, dispararía de nuevo al nuestro —
    // un bucle cruzado entre los dos scripts (visto en producción: la
    // fecha oscilando sin parar entre dos valores).
    desconectarObserversCompartidos();

    contenedores.forEach((contenedor) => {
      const mensaje = contenedor.querySelector("[data-entrega-estimada-mensaje]");
      const fechaEl = contenedor.querySelector("[data-entrega-estimada-fecha]");
      if (maxDiasGlobal > 0) {
        if (fechaEl && fechaEl.textContent !== textoFecha) fechaEl.textContent = textoFecha;
        if (mensaje) mensaje.hidden = false;
      } else if (mensaje) {
        mensaje.hidden = true;
      }
    });

    reconectarObserversCompartidos();

    if (maxDiasGlobal > 0) {
      guardarAtributoCarrito(isoFecha, textoFecha, localeUsado);
    }
  }

  // Init
  document.addEventListener("DOMContentLoaded", refresh);

  // Reintenta si el tema vuelve a pintar el carrito (drawer, AJAX, etc.)
  document.addEventListener("cart:updated", refresh);
  document.addEventListener("cart:refresh", refresh);

  // Recalcula si el cliente deja la pestaña abierta varios días y vuelve
  // (añadió productos hoy, entra a comprar la semana que viene sin recargar).
  // "pageshow" cubre también el caso de volver desde la caché del navegador
  // (botón atrás/adelante) sin que se dispare DOMContentLoaded de nuevo.
  window.addEventListener("pageshow", refresh);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refresh();
  });

  // Observa document.body en vez de un contenedor concreto: si el tema
  // reemplaza el nodo entero del carrito/drawer al re-renderizar (en vez de
  // solo su contenido interno), un observer atado al nodo antiguo se queda
  // "muerto" sin enterarse de nada. Observando body + subtree, siempre se
  // detecta el cambio esté donde esté. Se desconecta/reconecta en cada
  // refresh() (ver arriba) para no detectarse a sí mismo NI al otro script
  // (entrega-estimada-producto.js), gracias al registro compartido.
  let refreshPendiente = null;
  observer = new MutationObserver(() => {
    clearTimeout(refreshPendiente);
    refreshPendiente = setTimeout(refresh, 50);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.__entregaEstimadaObservers.push(observer);

  window.EntregaEstimada = { refresh };
})();